/**
 * Operator announcements — the Command Center **Announcements** tab (#490;
 * renamed from the "Headlines" block, ADR-0137).
 *
 * The dashboard split into tabs (ADR-0137; the Jumbotron numbers tab was
 * wiped by #740 — measurement rebuilds from the Contract). **Announcements**
 * is the news half: a feed of discrete announcements an operator posts and the
 * other reads walking in the door — "here's what's new, here's how the system works." This
 * is the OPERATOR-facing surface (#490), distinct from the firm-facing case-portal
 * announcements page (#492/#505); the two share a name, not an audience.
 *
 * Wire shape for /api/announcements. The server owns `id`, `postedAt`, `author`,
 * and `updatedAt` (stamped on create/edit); the client supplies only the editable
 * fields (title, body, pinned). Stored as a single JSON array in KV under one key
 * — one editor, low write-frequency, no concurrent-append race to guard.
 */

import { z } from "zod";

/** One posted announcement. Server-owned metadata + the authored content. */
export const AnnouncementSchema = z.object({
  /** Server-assigned stable id (crypto.randomUUID at create). */
  id: z.string(),
  /** The headline — required, non-empty. */
  title: z.string(),
  /** The detail body — may be empty (a title-only announcement is valid). */
  body: z.string(),
  /** Pinned announcements sort above the rest (standing reference content). */
  pinned: z.boolean(),
  /** Post date, YYYY-MM-DD (PT), stamped server-side at create. */
  postedAt: z.string(),
  /** Email of the operator who posted it, stamped server-side at create. */
  author: z.string(),
  /** Last-edit date, YYYY-MM-DD (PT); absent until first edited. */
  updatedAt: z.string().optional(),
});

export const AnnouncementsResponseSchema = z.object({
  items: z.array(AnnouncementSchema),
});

/** The editable fields a client sends to create or replace an announcement.
 *  `body` is optional (a title-only announcement is valid) and defaults to "". */
export const AnnouncementInputSchema = z.object({
  title: z.string().trim().min(1, "title required"),
  body: z.string().optional().default(""),
  pinned: z.boolean().optional(),
});

export type Announcement = z.infer<typeof AnnouncementSchema>;
export type AnnouncementsResponse = z.infer<typeof AnnouncementsResponseSchema>;
export type AnnouncementInput = z.infer<typeof AnnouncementInputSchema>;
